/**
 * SEOHead — Manages document <head> for SEO + GEO.
 * Sets title, meta description, Open Graph, Twitter cards, JSON-LD schemas.
 * Uses react-helmet-async for SSR-compatible head management.
 */

import { Helmet } from 'react-helmet-async';
import type { BlogPost } from '../../types/blog';
import {
  articleSchema,
  faqSchema,
  breadcrumbSchema,
  organizationSchema,
  type PageMeta,
} from '../../utils/seo';

interface SEOHeadProps {
  meta: PageMeta;
  post?: BlogPost;           // If provided, adds Article + FAQ schemas
  jsonLd?: object[];          // Additional JSON-LD schemas
}

export function SEOHead({ meta, post, jsonLd = [] }: SEOHeadProps) {
  const title = `${meta.title} | Good Atoms`;
  const schemas: object[] = [organizationSchema(), ...jsonLd];

  if (post) {
    schemas.push(articleSchema(post));
    if (post.faq?.length) {
      const faq = faqSchema(post.faq);
      if (faq) schemas.push(faq);
    }
    schemas.push(
      breadcrumbSchema([
        { name: 'Good Atoms', url: 'https://goodatoms.com' },
        { name: 'Undring', url: 'https://goodatoms.com/undring' },
        { name: post.title, url: `https://goodatoms.com/undring/${post.slug}` },
      ]),
    );
  }

  // Reflect the content language on <html> (Norwegian-first default).
  const htmlLang = meta.locale?.startsWith('en') ? 'en' : 'no';

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.url} />

      {/* hreflang alternates — tells search engines the EN/NO versions are
          the same article in two languages */}
      {meta.alternates?.map((alt) => (
        <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={meta.url} />
      <meta property="og:site_name" content="Good Atoms" />
      <meta property="og:type" content={meta.type || 'website'} />
      <meta property="og:locale" content={meta.locale || 'nb_NO'} />
      {meta.image && <meta property="og:image" content={meta.image} />}
      {meta.imageAlt && <meta property="og:image:alt" content={meta.imageAlt} />}
      {meta.publishedTime && (
        <meta property="article:published_time" content={meta.publishedTime} />
      )}
      {meta.modifiedTime && (
        <meta property="article:modified_time" content={meta.modifiedTime} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {meta.image && <meta name="twitter:image" content={meta.image} />}

      {/* JSON-LD structured data */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
